// ZONE / CLUSTER COLORS: "split the color wheel by the number of zones so we always get maximum
// contrast, and raise the luma and saturation. " Two decisions that make that actually deliver
// rather than only sounding right 1.

// TUNED AGAINST MEASURED GAMUT, not guessed. Lightness and saturation trade off against each other in
// sRGB — blues simply cannot be both bright and vivid — so these were picked by sweeping L x C and reading
// the worst-case hue: at L=0.78 the weakest hue fell to 0.33 saturation (a washed-out blue), while L=0.70
// holds >=0.47 across the whole wheel and still keeps every colour bright (min relative luminance ~0.31)
// with brightness even hue-to-hue (max/min ~1.2). More chroma than ~0.21 buys nothing — the gamut clamp
// eats it — so this is about as vivid as sRGB gets at an even, high lightness.
/* * Perceptual lightness: bright over a satellite basemap without washing the hues out. */
export const ZONE_L = 0.7;
/* * Perceptual chroma: as vivid as the gamut allows at that lightness. */
export const ZONE_C = 0.21;

const clamp01 = (x: number) => (x < 0 ? 0 : x > 1 ? 1 : x);

/* * OKLab → linear sRGB (Björn Ottosson's matrices). */
function oklabToLinearSrgb(
  L: number,
  a: number,
  b: number,
): [number, number, number] {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;
  return [
    +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ];
}

const linearToSrgb = (c: number) =>
  c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;

const inGamut = ([r, g, b]: [number, number, number]) =>
  r >= -1e-4 &&
  r <= 1 + 1e-4 &&
  g >= -1e-4 &&
  g <= 1 + 1e-4 &&
  b >= -1e-4 &&
  b <= 1 + 1e-4;

/*
 *
 * OKLCH → `#rrggbb`. sRGB can't hold `ZONE_C` at every hue (yellows reach far further than blues), so an
 * out-of-gamut colour has its CHROMA reduced until it fits — never its lightness. Dropping chroma keeps
 * every zone equally bright, which is what stops one zone reading as "shaded" next to its neighbour.
 */
export function oklchToHex(L: number, C: number, hDeg: number): string {
  const h = (hDeg * Math.PI) / 180;
  let lo = 0;
  let hi = C;
  let rgb = oklabToLinearSrgb(L, C * Math.cos(h), C * Math.sin(h));
  if (!inGamut(rgb)) {
    // binary-search the largest in-gamut chroma at this hue + lightness
    for (let i = 0; i < 24; i++) {
      const mid = (lo + hi) / 2;
      const test = oklabToLinearSrgb(L, mid * Math.cos(h), mid * Math.sin(h));
      if (inGamut(test)) lo = mid;
      else hi = mid;
    }
    rgb = oklabToLinearSrgb(L, lo * Math.cos(h), lo * Math.sin(h));
  }
  const hex = rgb
    .map((c) =>
      Math.round(clamp01(linearToSrgb(c)) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("");
  return `#${hex}`;
}

/*
 *
 * The largest stride < n/2 that is coprime with n — so walking slots by it visits every hue exactly once
 * (even spacing preserved) while consecutive zones land ~half a wheel apart. Falls back to 1 for n < 4
 * where "opposite" isn't meaningful and even division already maximises the gap.
 */
export function spreadStride(n: number): number {
  const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
  for (let s = Math.floor(n / 2); s >= 1; s--) if (gcd(s, n) === 1) return s;
  return 1;
}

/*
 *
 * `n` maximally-distinct zone colours: the OKLCH wheel divided into n equal steps at fixed high lightness
 * and chroma, ordered so consecutive indices are near-opposite hues.
 *
 * The 20° offset just starts the wheel at a warm red rather than magenta — zone 1 reads as a "first"
 * colour. Purely cosmetic; the spacing is unaffected.
 */
export function zonePalette(n: number): string[] {
  const count = Math.max(1, Math.floor(n));
  const stride = spreadStride(count);
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    const slot = (i * stride) % count;
    out.push(oklchToHex(ZONE_L, ZONE_C, 20 + (slot * 360) / count));
  }
  return out;
}

/*
 *
 * The colour for zone `i` when the palette holds `n`. Indices CYCLE within n (an out-of-range index reuses
 * a hue rather than silently enlarging the palette — growing it would shift every other zone's colour
 * which is the last thing you want while looking at a map).
 */
export function zoneColor(i: number, n = 12): string {
  const pal = zonePalette(n);
  return pal[((i % pal.length) + pal.length) % pal.length];
}

/*
 *
 * Why this exists: the map used to colour zones from a fixed six-entry list cycled by `zone_number
 * % 6` — a list that happened to hold three greens and two oranges, so a four-zone field showed two
 * greens side by side and read as a shade ramp rather than as four distinct zones. `zonePalette(n)`
 * splits the hue wheel by n, so it needs the REAL n, and the only place n is known is the data
 * itself.
 */
export function zoneNumbersIn(data: unknown): number[] {
  const fc = data as
    | { features?: { properties?: Record<string, unknown> | null }[] }
    | null
    | undefined;
  if (!fc?.features?.length) return [];
  const seen = new Set<number>();
  for (const f of fc.features) {
    const p = f?.properties ?? {};
    const raw = p.zone_number ?? p.zone;
    const n =
      typeof raw === "number"
        ? raw
        : typeof raw === "string"
          ? Number(raw)
          : NaN;
    if (Number.isFinite(n)) seen.add(n);
  }
  return [...seen].sort((a, b) => a - b);
}

/*
 *
 * Flat `[value, color, value, color, …]` pairs for a MapLibre `match` expression, assigning each zone
 * number its own slot in a palette sized to the zone count. Empty in → empty out, so the caller renders a
 * neutral fill rather than a confident colour for zones it cannot see.
 */
/*
 *
 * THE COLOURS MATCH WHAT THE EDITOR WILL PAINT — behaviour zones by their own class colours
 * relative zones from the shared palette sized to the zones present — so when the computed state
 * lands the picture does not change hue, only gains its cell detail.
 */
export function colorServedZoneFc(
  fc: GeoJSON.FeatureCollection,
  behaviorColorOf: ((zone: number) => string) | null,
): GeoJSON.FeatureCollection {
  const zoneOf = (f: {
    properties?: Record<string, unknown> | null;
  }): number | null => {
    const raw = f.properties?.zone_number ?? f.properties?.zone;
    return typeof raw === "number" && Number.isFinite(raw) ? raw : null;
  };
  const present = [
    ...new Set(
      (fc.features ?? []).map(zoneOf).filter((z): z is number => z != null),
    ),
  ].sort((a, b) => a - b);
  return {
    ...fc,
    features: (fc.features ?? []).map((f) => {
      const z = zoneOf(f);
      const color =
        z == null
          ? "#9ca3af"
          : behaviorColorOf
            ? behaviorColorOf(z)
            : zoneColor(
                Math.max(0, present.indexOf(z)),
                Math.max(1, present.length),
              );
      return { ...f, properties: { ...(f.properties ?? {}), color } };
    }),
  };
}

export function zoneMatchPairs(zoneNumbers: number[]): (number | string)[] {
  if (zoneNumbers.length === 0) return [];
  const pal = zonePalette(zoneNumbers.length);
  return zoneNumbers.flatMap((z, i) => [z, pal[i]]);
}
